import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Zap, ArrowUp, ArrowDown, Trophy, Sparkles, Check, X, Crown, ChevronRight } from "lucide-react";
import { useAuth } from "../components/AuthProvider";
import { playClick, playSelect, playAffirm, playError, playReveal } from "../lib/sounds";

const API = process.env.REACT_APP_BACKEND_URL;

/* Stages: category → prime → compare → reveal → trivia → done */

export default function PowerSmash() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [stage, setStage] = useState("category");
  const [categories, setCategories] = useState([]);
  const [game, setGame] = useState(null);
  const [primeId, setPrimeId] = useState(null);
  const [oppOrder, setOppOrder] = useState([]);
  const [oppIdx, setOppIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // {opponent_id, guess, time_taken, correct?, opponent_rank?}
  const [timerLeft, setTimerLeft] = useState(25);
  const [busy, setBusy] = useState(false);
  const startedAt = useRef(0);
  const compareStartAt = useRef(0);
  const [submitResult, setSubmitResult] = useState(null);
  const [trivia, setTrivia] = useState(null);
  const [triviaLeft, setTriviaLeft] = useState(20);
  const triviaStart = useRef(0);
  const [triviaResult, setTriviaResult] = useState(null);
  const [placed, setPlaced] = useState({}); // celeb_id -> rank (revealed)

  useEffect(() => {
    fetch(`${API}/api/power/categories`).then((r) => r.json()).then((d) => setCategories(d.categories || []));
  }, []);

  // Timer for compare stage
  useEffect(() => {
    if (stage !== "compare") return;
    const t = setInterval(() => {
      const elapsed = (Date.now() - compareStartAt.current) / 1000;
      const left = Math.max(0, 25 - elapsed);
      setTimerLeft(left);
      if (left <= 0) autoTimeout();
    }, 100);
    return () => clearInterval(t);
  }, [stage, oppIdx]);

  // Timer for trivia
  useEffect(() => {
    if (stage !== "trivia" || !trivia) return;
    const t = setInterval(() => {
      const elapsed = (Date.now() - triviaStart.current) / 1000;
      const left = Math.max(0, 20 - elapsed);
      setTriviaLeft(left);
      if (left <= 0) submitTrivia(-1);
    }, 100);
    return () => clearInterval(t);
  }, [stage, trivia]);

  const startGame = async (category_id, is_daily = false) => {
    playClick();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/power/new`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category_id, is_daily }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      setGame(d);
      setStage("prime");
    } catch (e) { toast.error(e.message); playError(); }
    finally { setBusy(false); }
  };

  const chooseCategory = (cid) => startGame(cid, false);
  const chooseDaily = () => startGame(null, true);

  const choosePrime = async (celebId) => {
    playSelect();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/power/${game.game_id}/prime`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prime_id: celebId }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      setPrimeId(celebId);
      setOppOrder(d.opponent_order);
      setOppIdx(0);
      setAnswers([]);
      setPlaced({});
      startedAt.current = Date.now();
      compareStartAt.current = Date.now();
      setStage("compare");
    } catch (e) { toast.error(e.message); playError(); }
    finally { setBusy(false); }
  };

  const currentOpponent = useMemo(() => {
    if (stage !== "compare" || !game) return null;
    const id = oppOrder[oppIdx];
    return game.grid.find((g) => g.id === id);
  }, [stage, game, oppOrder, oppIdx]);

  const primeCelebName = useMemo(() => {
    if (!game || !primeId) return "";
    return game.grid.find((g) => g.id === primeId)?.name || "";
  }, [game, primeId]);

  const answerCompare = async (guess) => {
    if (!currentOpponent || busy) return;
    playSelect();
    const time_taken = (Date.now() - compareStartAt.current) / 1000;
    const next = [...answers, { opponent_id: currentOpponent.id, guess, time_taken }];
    setAnswers(next);
    if (oppIdx + 1 < oppOrder.length) {
      setOppIdx(oppIdx + 1);
      compareStartAt.current = Date.now();
    } else {
      await finalSubmit(next);
    }
  };

  const autoTimeout = async () => {
    // Auto-lock a "lower" guess and advance (design choice; treats timeout as wrong).
    if (!currentOpponent) return;
    const time_taken = 25;
    const next = [...answers, { opponent_id: currentOpponent.id, guess: "lower", time_taken }];
    setAnswers(next);
    if (oppIdx + 1 < oppOrder.length) {
      setOppIdx(oppIdx + 1);
      compareStartAt.current = Date.now();
    } else {
      await finalSubmit(next);
    }
  };

  const finalSubmit = async (allAnswers) => {
    setBusy(true);
    const total_time = (Date.now() - startedAt.current) / 1000;
    try {
      const r = await fetch(`${API}/api/power/${game.game_id}/submit`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comparisons: allAnswers, total_time }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      setSubmitResult(d);
      // Animate placing each opponent into the grid one at a time
      const rankMap = {};
      d.reveal.forEach((c) => { rankMap[c.id] = c.rank; });
      setPlaced(rankMap);
      setStage("reveal");
      setTimeout(() => playReveal(), 400);
    } catch (e) { toast.error(e.message); playError(); }
    finally { setBusy(false); }
  };

  const startTrivia = async () => {
    playClick();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/power/${game.game_id}/trivia`);
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const t = await r.json();
      setTrivia(t);
      triviaStart.current = Date.now();
      setTriviaLeft(20);
      setStage("trivia");
    } catch (e) { toast.error(e.message); playError(); }
    finally { setBusy(false); }
  };

  const skipTrivia = async () => {
    playClick();
    await fetch(`${API}/api/power/${game.game_id}/finalize`, { method: "POST", credentials: "include" }).catch(() => {});
    setStage("done");
  };

  const submitTrivia = async (choice_index) => {
    if (busy) return;
    setBusy(true);
    const time_taken = (Date.now() - triviaStart.current) / 1000;
    try {
      const r = await fetch(`${API}/api/power/${game.game_id}/trivia`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice_index, time_taken }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      setTriviaResult(d);
      d.correct ? playAffirm() : playError();
      setStage("done");
    } catch (e) { toast.error(e.message); playError(); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="power-back-btn" onClick={() => nav("/home")}
          className="ml-12 flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FFD700]">
          <ArrowLeft size={16} /> Home
        </button>

        <div className="mt-4 flex items-center gap-2">
          <Zap size={18} className="text-[#FFD700]" />
          <span className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">Power Smash</span>
        </div>

        <AnimatePresence mode="wait">
          {stage === "category" && (
            <StageCategory key="cat" categories={categories}
              onPick={chooseCategory} onDaily={chooseDaily} busy={busy} />
          )}
          {stage === "prime" && game && (
            <StagePrime key="prime" grid={game.grid} category={game.category}
              onPick={choosePrime} busy={busy} />
          )}
          {stage === "compare" && game && (
            <StageCompare key="compare"
              grid={game.grid} primeId={primeId} primeName={primeCelebName}
              opponent={currentOpponent}
              answers={answers} oppTotal={oppOrder.length}
              oppIdx={oppIdx} timerLeft={timerLeft}
              placed={placed}
              onAnswer={answerCompare} />
          )}
          {stage === "reveal" && submitResult && (
            <StageReveal key="reveal" data={submitResult} game={game}
              onTrivia={startTrivia} onSkip={skipTrivia} />
          )}
          {stage === "trivia" && trivia && (
            <StageTrivia key="trivia" trivia={trivia} timerLeft={triviaLeft}
              onAnswer={submitTrivia} />
          )}
          {stage === "done" && (
            <StageDone key="done" submitResult={submitResult} triviaResult={triviaResult}
              onLeaderboard={() => nav("/leaderboards/power")}
              onAgain={() => { setStage("category"); setGame(null); setPrimeId(null); setAnswers([]); setPlaced({}); setSubmitResult(null); setTrivia(null); setTriviaResult(null); }}
              onLogin={login} authed={!!user} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ---------- Category picker ---------- */
const StageCategory = ({ categories, onPick, onDaily, busy }) => (
  <motion.div key="cat" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
    <h1 className="mt-3 font-display text-5xl leading-[0.85] tracking-tight">Pick a Category</h1>
    <p className="mt-2 text-sm text-[#FDFBF7]/70">9 celebrities. 8 rankings. One Prime.</p>
    <button data-testid="power-daily-btn" disabled={busy} onClick={onDaily}
      className="mt-5 w-full rounded-2xl py-4 font-bold text-[#0A090D] flex items-center justify-center gap-2"
      style={{ background: "linear-gradient(90deg,#FFD700,#FF6B35)", boxShadow: "0 0 20px rgba(255,215,0,0.35)" }}>
      <Sparkles size={16} /> Play Today&rsquo;s Daily Power Smash
    </button>
    <div className="mt-5 grid grid-cols-2 gap-2" data-testid="power-categories">
      {categories.map((c) => (
        <button key={c.id} data-testid={`power-cat-${c.id}`} disabled={busy}
          onClick={() => onPick(c.id)}
          className="text-left rounded-2xl border border-white/10 bg-black/40 p-3 hover:border-[#FFD700]/50 hover:bg-[#FFD700]/5 transition-all">
          <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">{c.difficulty}</div>
          <div className="mt-1 font-display text-lg leading-tight">{c.label}</div>
          <div className="mt-1 text-[10px] text-[#FDFBF7]/50">{c.celeb_count} celebs</div>
        </button>
      ))}
    </div>
  </motion.div>
);

/* ---------- Prime picker (3x3 grid) ---------- */
const StagePrime = ({ grid, category, onPick, busy }) => (
  <motion.div key="prime" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
    <h1 className="mt-3 font-display text-4xl leading-[0.9]">Choose your Smashimus Prime</h1>
    <p className="mt-1 text-sm text-[#FDFBF7]/70">{category.label} · Tap the one you'll defend.</p>
    <div className="mt-5 grid grid-cols-3 gap-2" data-testid="power-prime-grid">
      {grid.map((c, i) => (
        <motion.button
          key={c.id} data-testid={`power-celeb-${c.id}`} disabled={busy}
          onClick={() => onPick(c.id)}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.04 }}
          className="aspect-square rounded-2xl border-2 border-white/10 bg-gradient-to-br from-[#16141D] to-[#0A090D] p-2 flex items-center justify-center text-center hover:border-[#FFD700] hover:shadow-[0_0_20px_rgba(255,215,0,0.4)] active:scale-95 transition-all"
        >
          <div className="font-display text-sm leading-tight text-[#FDFBF7]">{c.name}</div>
        </motion.button>
      ))}
    </div>
  </motion.div>
);

/* ---------- Compare stage — hidden 3x3 + one opponent card ---------- */
const StageCompare = ({ grid, primeId, primeName, opponent, oppTotal, oppIdx, timerLeft, placed, onAnswer }) => {
  const urgent = timerLeft < 8;
  const pct = Math.max(0, Math.min(100, (timerLeft / 25) * 100));

  return (
    <motion.div key="compare" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FF107A]">Round {oppIdx + 1} / {oppTotal}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">
            Prime: <span className="text-[#FFD700] font-bold">{primeName}</span>
          </div>
        </div>
        {/* Timer bar */}
        <div className="mt-1.5 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="power-timer">
          <motion.div className="h-full rounded-full"
            style={{ background: urgent ? "#FF107A" : "#FFD700",
                     boxShadow: urgent ? "0 0 12px #FF107A" : "0 0 10px #FFD700" }}
            animate={{ width: `${pct}%` }} transition={{ duration: 0.1 }} />
        </div>
      </div>

      {/* Hidden ranking board */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-3" data-testid="power-board">
        <div className="text-[9px] tracking-[0.3em] uppercase text-[#FDFBF7]/50 mb-2 text-center">
          Hidden Ranking · Top-left = #1
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[1,2,3,4,5,6,7,8,9].map((rank) => {
            const revealed = Object.entries(placed).find(([, r]) => r === rank);
            const celeb = revealed ? grid.find((g) => g.id === revealed[0]) : null;
            const isPrime = celeb?.id === primeId;
            return (
              <div key={rank}
                data-testid={`power-cell-${rank}`}
                className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-center p-1 ${
                  celeb
                    ? isPrime
                      ? "border-[#FFD700] bg-[#FFD700]/15"
                      : "border-white/15 bg-black/50"
                    : "border-white/5 bg-black/60"
                }`}
              >
                <div className="text-[8px] tracking-[0.2em] uppercase text-[#FFD700]/70">#{rank}</div>
                <div className={`font-display text-[10px] leading-tight mt-0.5 ${
                  celeb ? "text-[#FDFBF7]" : "text-[#FDFBF7]/70"
                }`}>
                  {celeb ? celeb.name : "?"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Opponent card + higher/lower */}
      {opponent && (
        <motion.div key={opponent.id}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
          className="mt-4 rounded-3xl border border-[#FF107A]/40 bg-[#FF107A]/5 p-5 text-center"
          data-testid="power-opponent"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FF107A]">Opponent</div>
          <div className="mt-2 font-display text-4xl leading-[0.9] text-[#FDFBF7]" data-testid="power-opponent-name">
            {opponent.name}
          </div>
          <div className="mt-3 text-sm text-[#FDFBF7]/70">
            Is <span className="text-[#FFD700] font-bold">{primeName}</span> ranked higher or lower?
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button data-testid="power-higher-btn" onClick={() => onAnswer("higher")}
              className="rounded-2xl bg-[#22C55E]/15 border-2 border-[#22C55E]/60 hover:bg-[#22C55E]/25 py-4 font-display text-xl flex items-center justify-center gap-2 active:scale-95 transition-all">
              <ArrowUp size={18} /> Higher
            </button>
            <button data-testid="power-lower-btn" onClick={() => onAnswer("lower")}
              className="rounded-2xl bg-[#FF107A]/15 border-2 border-[#FF107A]/60 hover:bg-[#FF107A]/25 py-4 font-display text-xl flex items-center justify-center gap-2 active:scale-95 transition-all">
              <ArrowDown size={18} /> Lower
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

/* ---------- Reveal — score card + trivia CTA ---------- */
const StageReveal = ({ data, game, onTrivia, onSkip }) => {
  const confetti = data.correct === 8 ? Array.from({ length: 40 }).map((_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 0.3,
    duration: 1.6 + Math.random() * 1.2,
    color: ["#FFD700", "#FF107A", "#00E5FF", "#A855F7"][i % 4],
    rot: Math.random() * 360,
  })) : [];

  return (
    <motion.div key="reveal" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      {confetti.length > 0 && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
          {confetti.map((c) => (
            <motion.div key={c.id}
              initial={{ y: -20, rotate: 0, opacity: 1 }}
              animate={{ y: "110vh", rotate: c.rot }}
              transition={{ duration: c.duration, delay: c.delay, ease: "easeIn" }}
              className="absolute w-2 h-3"
              style={{ left: `${c.left}%`, background: c.color, boxShadow: `0 0 6px ${c.color}` }} />
          ))}
        </div>
      )}
      <div className="mt-4 rounded-3xl border border-[#FFD700]/40 p-5 relative overflow-hidden"
        style={{ background: "radial-gradient(circle at 20% 15%, rgba(255,215,0,0.28), transparent 55%), #16141D" }}
        data-testid="power-reveal">
        <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700] flex items-center gap-1">
          <Crown size={12} /> Crowning Your Smashimus Prime
        </div>
        <div className="mt-2 font-display text-5xl leading-[0.9] tabular text-[#FFD700]" data-testid="power-score">
          {data.base_score}
        </div>
        <div className="mt-1 text-sm text-[#FDFBF7]/80">
          {data.correct} / 8 correct · {data.accuracy_pct}% accuracy
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label="Comparisons" value={data.comparison_pts} accent="#00E5FF" />
        <Stat label="Perfect Bonus" value={data.perfect_bonus} accent="#FF107A" />
        <Stat label="Speed Bonus" value={data.speed_bonus} accent="#FFD700" />
      </div>

      {/* Full ranking reveal */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="power-full-ranking">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/60 mb-2">Final Ranking</div>
        <div className="space-y-1">
          {data.reveal.map((c) => (
            <div key={c.id}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                c.is_prime ? "border-[#FFD700]/70 bg-[#FFD700]/10" : "border-white/10 bg-black/40"
              }`}>
              <div className="w-6 text-center font-display text-lg tabular text-[#FDFBF7]/70">{c.rank}</div>
              <div className="flex-1 text-sm font-semibold text-[#FDFBF7]">
                {c.name}
                {c.is_prime && (
                  <span className="text-[9px] tracking-[0.2em] uppercase text-[#FFD700] ml-2">Your Prime</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.trivia_qualified ? (
        <button data-testid="power-trivia-cta" onClick={onTrivia}
          className="mt-5 w-full rounded-full py-3 font-bold text-[#0A090D] flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(90deg,#FFD700,#FF6B35)" }}>
          Bonus Trivia Round <ChevronRight size={16} />
        </button>
      ) : (
        <button data-testid="power-skip-trivia" onClick={onSkip}
          className="mt-5 w-full rounded-full bg-[#FDFBF7] text-[#0A090D] py-3 font-bold">
          Continue
        </button>
      )}
    </motion.div>
  );
};

/* ---------- Trivia stage ---------- */
const StageTrivia = ({ trivia, timerLeft, onAnswer }) => {
  const urgent = timerLeft < 6;
  return (
    <motion.div key="trivia" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <div className="mt-3 flex items-baseline justify-between">
        <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">Bonus Trivia</div>
        <div className="text-[10px] uppercase text-[#FDFBF7]/60">Prime: <span className="text-[#FFD700] font-bold">{trivia.prime.name}</span></div>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-white/10 overflow-hidden">
        <motion.div className="h-full rounded-full"
          style={{ background: urgent ? "#FF107A" : "#FFD700",
                   boxShadow: urgent ? "0 0 12px #FF107A" : "0 0 10px #FFD700" }}
          animate={{ width: `${(timerLeft / 20) * 100}%` }} transition={{ duration: 0.1 }} />
      </div>
      <h2 data-testid="power-trivia-question" className="mt-5 font-display text-2xl leading-[1.05]">{trivia.question}</h2>
      <div className="mt-4 space-y-2">
        {trivia.choices.map((c, i) => (
          <button key={i} data-testid={`power-trivia-choice-${i}`} onClick={() => onAnswer(i)}
            className="w-full text-left rounded-2xl border-2 border-white/15 bg-black/30 hover:border-[#FFD700]/60 hover:bg-[#FFD700]/10 px-4 py-3 font-semibold text-[#FDFBF7] active:scale-[0.98] transition-all">
            {String.fromCharCode(65 + i)}. {c}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

/* ---------- Done ---------- */
const StageDone = ({ submitResult, triviaResult, onAgain, onLeaderboard, onLogin, authed }) => {
  const total = triviaResult?.final_score ?? submitResult?.base_score ?? 0;
  return (
    <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <div className="mt-5 text-center">
        <div className="inline-flex w-16 h-16 rounded-2xl bg-[#FFD700]/15 border border-[#FFD700]/50 items-center justify-center mb-4">
          <Trophy size={26} className="text-[#FFD700]" />
        </div>
        <div className="font-display text-6xl leading-[0.9] tabular text-[#FFD700]" data-testid="power-final-score">{total}</div>
        <div className="mt-2 text-sm text-[#FDFBF7]/70">Final Score</div>
      </div>
      {triviaResult && (
        <div data-testid="power-trivia-result"
          className={`mt-5 rounded-2xl border-2 p-4 ${
            triviaResult.correct ? "border-[#22C55E] bg-[#22C55E]/10" : "border-[#FF107A]/50 bg-[#FF107A]/10"
          }`}>
          <div className="flex items-center gap-2">
            {triviaResult.correct ? <Check size={20} className="text-[#22C55E]" /> : <X size={20} className="text-[#FF107A]" />}
            <div className="font-display text-2xl">
              {triviaResult.correct ? `Trivia correct! +${triviaResult.trivia_pts + triviaResult.trivia_speed_bonus}` : "Trivia missed."}
            </div>
          </div>
          {!triviaResult.correct && triviaResult.correct_answer && (
            <div data-testid="power-trivia-correct-answer"
              className="mt-2 text-sm text-[#FDFBF7]/80">
              Correct answer: <span className="text-[#FFD700] font-bold">{triviaResult.correct_answer}</span>
            </div>
          )}
        </div>
      )}
      <div className="mt-6 space-y-2">
        <button data-testid="power-again" onClick={onAgain}
          className="w-full rounded-full py-3 font-bold text-[#0A090D]"
          style={{ background: "linear-gradient(90deg,#FFD700,#FF6B35)" }}>
          Play Again
        </button>
        <button data-testid="power-leaderboard" onClick={onLeaderboard}
          className="w-full rounded-full border border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold flex items-center justify-center gap-2">
          <Trophy size={16} /> Power Smash Leaderboard
        </button>
        {!authed && (
          <button onClick={onLogin} className="w-full rounded-full bg-[#FDFBF7] text-[#0A090D] py-3 font-bold">
            Sign in to save your score
          </button>
        )}
      </div>
    </motion.div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 px-2 py-3 text-center">
    <div className="font-display text-xl leading-none tabular" style={{ color: accent }}>{value}</div>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-1">{label}</div>
  </div>
);
